class TootoPlasmaPoints extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.dialog = this.querySelector('[data-tooto-plasma-modal]');
    this.openButton = this.querySelector('[data-tooto-plasma-modal-open]');
    this.closeButton = this.querySelector('[data-tooto-plasma-modal-close]');
    this.viewButtons = Array.from(this.querySelectorAll('[data-tooto-plasma-view]'));
    this.modalVideos = Array.from(this.querySelectorAll('.tooto-plasma-points__modal-video'));

    this.addEventListener('click', this.handleClick);
    this.dialog?.addEventListener('keydown', this.handleDialogKeydown);

    if (this.dialog) this.setView(this.dialog.dataset.view || 'bottom', false);

    if (!('IntersectionObserver' in window)) {
      this.classList.add('is-visible');
      return;
    }

    this.observer = new IntersectionObserver(
      async ([entry]) => {
        if (!entry.isIntersecting) return;
        this.observer.disconnect();

        const images = Array.from(this.querySelectorAll('.tooto-plasma-points__layer-image'));
        await Promise.all(
          images.map((image) => {
            if (image.complete) return Promise.resolve();
            return new Promise((resolve) => {
              image.addEventListener('load', resolve, { once: true });
              image.addEventListener('error', resolve, { once: true });
            });
          })
        );

        if (!this.isConnected) return;
        requestAnimationFrame(() => this.classList.add('is-visible'));
      },
      { threshold: 0.45, rootMargin: '0px 0px -12% 0px' }
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.removeEventListener('click', this.handleClick);
    this.dialog?.removeEventListener('keydown', this.handleDialogKeydown);
    window.clearTimeout(this.closeTimer);
    this.pauseModalVideos();
  }

  handleClick = (event) => {
    if (event.target.closest('[data-tooto-plasma-modal-open]')) {
      this.openModal();
      return;
    }

    if (event.target.closest('[data-tooto-plasma-modal-close]')) {
      this.closeModal();
      return;
    }

    const viewButton = event.target.closest('[data-tooto-plasma-view]');
    if (viewButton) this.setView(viewButton.dataset.tootoPlasmaView, true);
  };

  handleDialogKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeModal();
      return;
    }

    if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;

    const activeIndex = this.viewButtons.findIndex((button) => button.getAttribute('aria-selected') === 'true');
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const nextIndex = (activeIndex + direction + this.viewButtons.length) % this.viewButtons.length;
    const nextButton = this.viewButtons[nextIndex];

    event.preventDefault();
    this.setView(nextButton.dataset.tootoPlasmaView, true);
    nextButton.focus();
  };

  openModal() {
    if (!this.dialog || this.dialog.dataset.open === 'true') return;

    this.lastFocusedElement = document.activeElement;
    this.dialog.classList.remove('is-closing');
    this.classList.add('is-detail-open');
    this.openButton?.setAttribute('aria-expanded', 'true');
    this.dialog.dataset.open = 'true';
    this.dialog.setAttribute('aria-hidden', 'false');

    requestAnimationFrame(() => {
      this.dialog.classList.add('is-open');
      this.setView(this.dialog.dataset.view || 'bottom', true);
      this.closeButton?.focus({ preventScroll: true });
    });
  }

  closeModal() {
    if (this.dialog?.dataset.open !== 'true' || this.dialog.classList.contains('is-closing')) return;

    this.dialog.classList.remove('is-open');
    this.dialog.classList.add('is-closing');
    window.clearTimeout(this.closeTimer);

    this.closeTimer = window.setTimeout(() => {
      this.pauseModalVideos();
      this.dialog.dataset.open = 'false';
      this.dialog.setAttribute('aria-hidden', 'true');
      this.dialog.classList.remove('is-closing', 'is-switching');
      this.classList.remove('is-detail-open');
      this.openButton?.setAttribute('aria-expanded', 'false');
      this.lastFocusedElement?.focus?.({ preventScroll: true });
    }, 480);
  }

  setView(view, animate = true) {
    if (!this.dialog || !['bottom', 'seamless'].includes(view)) return;

    this.dialog.dataset.view = view;
    this.viewButtons.forEach((button) => {
      const isSelected = button.dataset.tootoPlasmaView === view;
      button.setAttribute('aria-selected', String(isSelected));
      button.tabIndex = isSelected ? 0 : -1;
    });

    this.querySelectorAll('[data-tooto-plasma-panel]').forEach((panel) => {
      panel.setAttribute('aria-hidden', String(panel.dataset.tootoPlasmaPanel !== view));
    });

    this.updateModalVideoPlayback(view);

    this.dialog.classList.remove('is-switching');
    if (!animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    void this.dialog.offsetWidth;
    this.dialog.classList.add('is-switching');
  }

  updateModalVideoPlayback(view) {
    this.modalVideos.forEach((video) => {
      const shouldPlay = this.dialog.dataset.open === 'true' && view === 'bottom';

      if (!shouldPlay) {
        video.pause();
        return;
      }

      const playPromise = video.play();
      playPromise?.catch?.(() => {});
    });
  }

  pauseModalVideos() {
    this.modalVideos.forEach((video) => video.pause());
  }
}

if (!customElements.get('tooto-plasma-points')) {
  customElements.define('tooto-plasma-points', TootoPlasmaPoints);
}
